import React, { useState, useMemo, useEffect } from 'react';
import { getFieldValue } from '../../../contentful/mappers';
import Button from '../../Button/Button';
import './GoogleCalendar.css';

function GoogleCalendar({ data }) {
  const headline = getFieldValue(data, 'headline');
  const calendarsData = getFieldValue(data, 'calendars');

  // Parse calendars from Contentful
  // The calendars field is an Object type that may be stored as JSON string or object
  const calendars = useMemo(() => {
    if (!calendarsData) return [];
    
    try {
      // If it's a string, parse it as JSON
      let calendarsArray = calendarsData;
      if (typeof calendarsData === 'string') {
        calendarsArray = JSON.parse(calendarsData);
      }
      
      // If it's already an array, use it directly
      if (Array.isArray(calendarsArray)) {
        const seenIds = new Set();
        return calendarsArray
          .map((cal, index) => {
            const id = cal.id || cal.calendarId || '';
            if (!id) return null;
            if (seenIds.has(id)) {
              return null;
            }
            seenIds.add(id);
            return {
              id,
              name: cal.name || `Calendar ${index + 1}`,
              enabled: true,
              // Google Calendar predefined colors
              color: [
                '#7986cb', // Blue
                '#ef6c00', // Orange  
                '#d81b60', // Pink
                '#f09300', // Orange-yellow
                '#d50000', // Red
                '#ad1457', // Pink-red
                '#c0ca33', // Yellow-green
                '#7b1fa2', // Purple
                '#00897b', // Teal
                '#5e35b1', // Deep purple
              ][index % 10] || '#7986cb',
            };
          })
          .filter(Boolean); // Filter out any calendars without valid/duplicate IDs
      }
      
      return [];
    } catch (error) {
      console.error('Error parsing calendars from Contentful:', error);
      return [];
    }
  }, [calendarsData]);

  // State to track which calendars are enabled
  const [enabledCalendars, setEnabledCalendars] = useState({});

  // Initialize enabled calendars when calendars are parsed
  useEffect(() => {
    if (calendars.length > 0) {
      const calendarIds = calendars.map(cal => cal.id).sort().join(',');
      const currentIds = Object.keys(enabledCalendars).sort().join(',');
      
      // Only initialize if the calendar IDs don't match (first load or calendars changed)
      if (calendarIds !== currentIds) {
        const initialEnabled = calendars.reduce((acc, cal) => {
          acc[cal.id] = true;
          return acc;
        }, {});
        setEnabledCalendars(initialEnabled);
      }
    }
  }, [calendars, enabledCalendars]);

  // Build the calendar URL with only enabled calendars
  const calendarSrc = useMemo(() => {
    if (calendars.length === 0) return null;
    
    // Get enabled calendars with their IDs and colors
    const enabledCalendarsList = calendars.filter(cal => enabledCalendars[cal.id]);
    
    if (enabledCalendarsList.length === 0) return null;
    
    try {
      // Build the base URL manually to properly handle color encoding
      const baseUrl = 'https://calendar.google.com/calendar/embed';
      const queryParts = [];
      
      // Hide Google Calendar's own title bar (we show our own headline)
      queryParts.push('showTitle=0');

      // Add timezone (default to Europe/London)
      queryParts.push(`ctz=${encodeURIComponent('Europe/London')}`);
      
      // Add each enabled calendar with its color
      // Google Calendar expects src and color parameters in pairs
      // Colors must be in format: %23RRGGBB (where %23 is URL-encoded #)
      enabledCalendarsList.forEach(cal => {
        queryParts.push(`src=${encodeURIComponent(cal.id)}`);
        // Remove # and add %23 for proper URL encoding
        const colorWithoutHash = cal.color.replace('#', '');
        queryParts.push(`color=%23${colorWithoutHash}`);
      });
      
      const finalUrl = `${baseUrl}?${queryParts.join('&')}`;
      return finalUrl;
    } catch (error) {
      console.error('Error building calendar URL:', error);
      return null;
    }
  }, [calendars, enabledCalendars]);

  // Selection state helpers for master toggle and iframe visibility
  const totalCalendars = calendars.length;
  const selectedCount = calendars.reduce(
    (count, cal) => count + (enabledCalendars[cal.id] ? 1 : 0),
    0
  );
  const hasSelectedCalendars = selectedCount > 0;
  const allSelected = totalCalendars > 0 && selectedCount === totalCalendars;
  const someSelected = selectedCount > 0 && !allSelected;

  const toggleCalendar = (calendarId) => {
    setEnabledCalendars((prev) => ({
      ...prev,
      [calendarId]: !prev[calendarId],
    }));
  };

  const toggleAllCalendars = () => {
    setEnabledCalendars(() => {
      const next = {};
      const enable = !allSelected;
      calendars.forEach(cal => {
        next[cal.id] = enable;
      });
      return next;
    });
  };

  // Add selected calendars to Google Calendar
  const handleAddToGoogleCalendar = () => {
    const enabledCalendarsList = calendars.filter(cal => enabledCalendars[cal.id]);
    
    if (enabledCalendarsList.length === 0) {
      alert('Please select at least one calendar to add.');
      return;
    }

    // Google Calendar URL format to add multiple calendars: 
    // https://calendar.google.com/calendar/r?cid={calendarId1}&cid={calendarId2}&cid={calendarId3}...
    const calendarIds = enabledCalendarsList.map(cal => {
      return `cid=${encodeURIComponent(cal.id)}`;
    }).join('&');
    
    const addCalendarUrl = `https://calendar.google.com/calendar/r?${calendarIds}`;
    
    // Open single tab with all calendars
    window.open(addCalendarUrl, '_blank');
  };

  if (!data) return null;

  if (calendars.length === 0) {
    return (
      <section className="google-calendar-section">
        <div className="container">
          {headline && <h2 className="section-headline headline-visible">{headline}</h2>}
          <div className="calendar-error">
            <p>No calendars configured. Please add calendars in Contentful.</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="google-calendar-section">
      <div className="container">
        {headline && <h2 className="section-headline headline-visible">{headline}</h2>}
        <div className="calendar-controls-wrapper">
          {calendars.length > 0 && (
            <div className="calendar-checkboxes">
              <div className="calendar-checkboxes-header">
                <button
                  type="button"
                  className="calendar-master-toggle"
                  onClick={toggleAllCalendars}
                  aria-label={allSelected ? 'Select none' : 'Select all'}
                  title={
                    allSelected
                      ? 'Select none'
                      : someSelected
                        ? 'Select all'
                        : 'Select all'
                  }
                >
                  <span
                    className={
                      'calendar-checkbox-indicator calendar-checkbox-indicator-master' +
                      (allSelected ? ' calendar-checkbox-indicator-all' : '') +
                      (someSelected ? ' calendar-checkbox-indicator-some' : '')
                    }
                    style={{ 
                      '--calendar-color': 'var(--kc-space-opera)',
                      backgroundColor: (allSelected || someSelected)
                        ? 'var(--kc-space-opera)'
                        : 'transparent'
                    }}
                  />
                </button>
                <h3 className="calendar-checkboxes-title">Calendars</h3>
              </div>
              {calendars.map((cal) => (
                <label key={cal.id} className="calendar-checkbox-label">
                  <input
                    type="checkbox"
                    checked={enabledCalendars[cal.id] || false}
                    onChange={() => toggleCalendar(cal.id)}
                    className="calendar-checkbox"
                  />
                  <span 
                    className="calendar-checkbox-indicator"
                    style={{ 
                      '--calendar-color': cal.color,
                      backgroundColor: enabledCalendars[cal.id] ? cal.color : 'transparent'
                    }}
                  />
                  <span className="calendar-checkbox-text">{cal.name}</span>
                </label>
              ))}
              <div className="calendar-download-section">
                <Button 
                  onClick={handleAddToGoogleCalendar}
                  className="calendar-download-button"
                >
                  Add to Google Calendar
                </Button>
              </div>
            </div>
          )}
          <div className="calendar-wrapper">
            {hasSelectedCalendars && calendarSrc ? (
              <iframe
                key={calendarSrc} // Force re-render when URL changes
                src={calendarSrc}
                className="calendar-iframe"
                title="Google Calendar"
                frameBorder="0"
                scrolling="no"
                allowFullScreen
              />
            ) : (
              <div className="calendar-empty-message">
                <p>Please select at least one calendar.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

export default GoogleCalendar;
